//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { shouldInitWithAutomatedBrowser } from '@utils/envUtils';

import { ICuralateSession } from 'app/basic-session/ICuralateSession';
import { crl8 } from 'app/curalate.globals';

// Import the snippet for its side effects, then use a private
// pendoLib to capture the value.
import 'app/initialization/pendo/pendo-snippet';

interface QueuedEvent {
  metadata?: pendo.Metadata;
  trackType: string;
}

const MAX_QUEUE_SIZE = 50;

export class PendoTracker {
  // The pendo param is optional because some ad blockers
  // prevent the snippet we import above from loading. If
  // that snippet is blocked, window.pendo will be undefined
  // at runtime.
  constructor(private readonly pendo?: pendo.Pendo) {}

  // Events tracked before pendo.initialize() completes are buffered here
  // and replayed once init succeeds. Capped at MAX_QUEUE_SIZE to prevent
  // unbounded memory growth if init never completes.
  private eventQueue: QueuedEvent[] = [];

  private initCalled = false;

  // Drain the event queue by re-dispatching each event through track(), which
  // will now send them immediately since initCalled is true.
  private flushQueue() {
    const events = this.eventQueue;
    this.eventQueue = [];
    for (const event of events) {
      this.track(event.trackType, event.metadata);
    }
  }

  public init(
    release: string,
    hostname: string,
    session: ICuralateSession,
    ignoreAutomatedBrowser?: boolean,
  ) {
    if (this.isInitialized()) {
      return;
    } else if (
      !this.pendo ||
      !shouldInitWithAutomatedBrowser(
        release,
        hostname,
        navigator.webdriver,
        ignoreAutomatedBrowser,
      )
    ) {
      return;
    }

    this.pendo.initialize({
      account: {
        creationDate: moment(session.client.createdTimestamp).toDate(),
        groupId: session.group.id.toString(),
        id: session.client.id,
        name: session.client.name,
        salesforceBvSiteId: session.client.salesforceBvSiteId ?? '',
      },
      visitor: {
        email: session.user.email,
        full_name: `${session.user.firstName} ${session.user.lastName}`,
        id: session.user.id.toString(),
        isCuralateEmployee: session.internalInfo.isCuralateEmployee,
        portalGuid: session.user.portalGuid,
      },
    });

    this.initCalled = true;

    // Now that pendo is ready, flush any events that were queued
    // while we waited for initialization.
    this.flushQueue();
  }

  public isInitialized() {
    return this.initCalled;
  }

  public track(trackType: string, metadata?: pendo.Metadata) {
    if (this.initCalled) {
      this.pendo?.track(trackType, {
        sessionURL: crl8.logger.cloud.getCurrentSessionUrl() ?? '',
        ...metadata,
      });
    } else if (this.eventQueue.length < MAX_QUEUE_SIZE) {
      this.eventQueue.push({ metadata, trackType });
    }
  }
}

export const pendoTracker = new PendoTracker(pendo);
